import { ICreateUserDto } from "@/lib/interfaces/User/createUserInterface";
import { deepCopy } from "@/lib/utility/deep-copy";
import { UserEntity } from "@/models/userEntity";
import UserService from "@/services/mrfiktiv/services/userService";
import store from "@/store/VuexPlugin";
import { AuthRolesEnum } from "@/store/enum/authRolesEnum";
import Vue from "vue";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { IUserPermissionUpdateDto } from "../interface/user.permission.dto.interface";
import { IUserRoleUpdateDto } from "../interface/user.role.dto.interface";
import { ErrorLogModule } from "./error-log";
import { MrfiktivUpdateUserDtoGen } from "@/services/mrfiktiv/v1/data-contracts";

@Module({
  dynamic: true,
  namespaced: true,
  name: "userAdministrationStore",
  store
})
class UserAdministrationStore extends VuexModule {
  users: UserEntity[] = [];
  selectedUser: UserEntity = {} as any;

  _emptyUserEntity: UserEntity = {
    _id: "",
    userName: "",
    firstName: "",
    lastName: "",
    contact: {
      email: "",
      phone: ""
    },
    address: {},
    roles: [],
    authInfo: "" as any
  };

  @Mutation
  setSelectedUser(user: UserEntity) {
    this.selectedUser = user;
  }

  @Mutation
  addUserToUsers(user: UserEntity) {
    this.users.push(user);
  }

  @Mutation
  setSelectedUserRoles(roles: AuthRolesEnum[]) {
    this.selectedUser.roles = roles;
  }

  @Mutation
  setUsers(users: UserEntity[]) {
    this.users = users;
  }

  @Action
  async getSelectedUser(userId: string) {
    try {
      this.context.commit("setSelectedUser", deepCopy(this._emptyUserEntity));
      const user = await UserService.get(userId);
      this.context.commit("setSelectedUser", user);
      return user;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: `Could not load User as selected ${userId}`
      });
    }
  }

  @Action
  async getUsers() {
    try {
      const users = await UserService.getAll();
      this.context.commit("setUsers", users);
      return users;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not load Users"
      });
    }
  }

  @Action
  async removePermission(updatePermission: IUserPermissionUpdateDto) {
    try {
      const updatedUser = await UserService.removePermission(updatePermission);
      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not remove Permission from User."
      });
    }
  }

  @Action
  async addPermissions(user: UserEntity) {
    try {
      const updatedUser = await UserService.addPermissions(user);
      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not add Permission to User."
      });
    }
  }

  @Action
  async updateSelectedUserRoles(dto: IUserRoleUpdateDto) {
    try {
      this.context.commit("setSelectedUserRoles", dto.role);
      const updatedUser = await UserService.updateRole(dto);
      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not update User Roles"
      });
    }
  }
  @Action
  async updateUser(userDto: { id: string; updateUserDto: MrfiktivUpdateUserDtoGen }) {
    try {
      const user = await UserService.update(userDto.id, userDto.updateUserDto);
      this.context.commit("setSelectedUser", user);
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not update the User from UserAdministration Module."
      });
    }
  }

  @Action
  async invite(createUser: ICreateUserDto) {
    try {
      const createdUser = await UserService.invite(createUser);
      this.context.commit("setSelectedUser", createdUser);
      this.context.commit("addUserToUsers", createdUser);
      return createdUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not fetch current User"
      });
    }
  }

  @Action
  async resendInvitation() {
    const user = await UserService.resendInvitation(this.selectedUser._id);

    return user;
  }
}

export const UserAdministrationModule = getModule(UserAdministrationStore);
